import type { CurvaDocument } from "@/document/model/document";
import { getActivePage } from "@/document/model/document";
import { frameHasScript } from "@/document/model/frameScript";
import { getCachedSceneTransformCache, nodeSelectionCorners } from "@/document/operations/nodeWorldTransform";
import { createDefaultCamera } from "@/renderer/camera";
import {
  FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX,
  frameLabelGeometryForCorners,
  shouldShowFrameLabels,
} from "@/renderer/frameLabelGeometry";
import type { FrameLabelOverlay as RendererFrameLabelOverlay, RenderPreview } from "@/renderer/overlayTypes";
import type { Rect } from "@/lib/math/rect";
import type { FrameLabelOverlay } from "./canvasWorkspaceTypes";

function rectsIntersect(left: Rect, right: Rect): boolean {
  return (
    left.x <= right.x + right.width &&
    left.x + left.width >= right.x &&
    left.y <= right.y + right.height &&
    left.y + left.height >= right.y
  );
}

export function frameLabelOverlays(
  document: CurvaDocument,
  camera: ReturnType<typeof createDefaultCamera>,
  previews: RenderPreview[],
  activePageId?: string,
  viewportBounds?: Rect,
  options?: { allowScripts?: boolean },
): FrameLabelOverlay[] {
  if (!shouldShowFrameLabels(camera.zoom)) {
    return [];
  }

  const transformCache = getCachedSceneTransformCache(document, previews);
  const pageChildren = getActivePage(document, activePageId)?.children ?? [];
  return pageChildren.flatMap(node => {
    if ((node.type !== "frame" && node.type !== "component") || !node.visible) {
      return [];
    }

    if (viewportBounds) {
      const subtree = transformCache.subtreeInteractionBounds(node.id);
      if (subtree && !rectsIntersect(subtree, viewportBounds)) {
        return [];
      }
    }

    const hasScript = Boolean(options?.allowScripts && frameHasScript(document, node));
    const frameCorners = transformCache.selectionCorners(node.id);
    const geometry = frameCorners
      ? frameLabelGeometryForCorners(frameCorners, camera.zoom, {
        extraWidthPx: hasScript ? FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX : 0,
      })
      : undefined;
    if (!geometry) {
      return [];
    }
    const screenCorners = geometry.corners.map(point => documentPointToScreen(point, camera));
    const origin = screenCorners[0]!;
    return [{
      id: node.id,
      name: node.name,
      kind: node.type,
      hasScript,
      left: origin.x,
      top: origin.y,
      width: geometry.width * camera.zoom,
      height: geometry.height * camera.zoom,
      rotation: geometry.rotation,
      corners: screenCorners,
    }];
  });
}

export function frameLabelRenderItems(
  document: CurvaDocument,
  previews: RenderPreview[],
  renamingFrameId: string | undefined,
  activePageId?: string,
  options?: { allowScripts?: boolean },
): RendererFrameLabelOverlay[] {
  const transformCache = getCachedSceneTransformCache(document);
  const previewBoundsById = new Map(previews.map(preview => [preview.nodeId, preview.bounds] as const));
  const pageChildren = getActivePage(document, activePageId)?.children ?? [];
  return pageChildren.flatMap(node => {
    if ((node.type !== "frame" && node.type !== "component") || !node.visible || node.id === renamingFrameId) {
      return [];
    }

    const corners = previewBoundsById.has(node.id)
      ? nodeSelectionCorners(document, node.id, previews)
      : transformCache.selectionCorners(node.id);
    if (!corners) {
      return [];
    }

    return [{
      id: node.id,
      name: node.name,
      kind: node.type,
      hasScript: Boolean(options?.allowScripts && frameHasScript(document, node)),
      bounds: previewBoundsById.get(node.id) ?? node.bounds,
      corners,
    }];
  });
}

export function hitTestFrameLabel(labels: FrameLabelOverlay[], clientX: number, clientY: number): FrameLabelOverlay | undefined {
  for (let index = labels.length - 1; index >= 0; index -= 1) {
    const label = labels[index];
    if (!label) {
      continue;
    }

    if (pointInPolygon({ x: clientX, y: clientY }, label.corners)) {
      return label;
    }
  }

  return undefined;
}

/** Which script action strip button was hit, if any (`edit` = left, `run` = right). */
export function hitTestFrameLabelScriptAction(
  label: FrameLabelOverlay,
  clientX: number,
  clientY: number,
): "edit" | "run" | undefined {
  if (!label.hasScript) {
    return undefined;
  }

  const dx = clientX - label.left;
  const dy = clientY - label.top;
  const radians = -label.rotation * Math.PI / 180;
  const localX = dx * Math.cos(radians) - dy * Math.sin(radians);
  const localY = dx * Math.sin(radians) + dy * Math.cos(radians);
  if (localY < 0 || localY > label.height) {
    return undefined;
  }

  if (localX < 0 || localX > FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX) {
    return undefined;
  }

  const mid = FRAME_LABEL_SCRIPT_ACTIONS_WIDTH_PX * 0.5;
  return localX < mid ? "edit" : "run";
}

function documentPointToScreen(point: { x: number; y: number }, camera: ReturnType<typeof createDefaultCamera>): { x: number; y: number } {
  return {
    x: (point.x - camera.x) * camera.zoom,
    y: (point.y - camera.y) * camera.zoom,
  };
}

function pointInPolygon(point: { x: number; y: number }, polygon: Array<{ x: number; y: number }>): boolean {
  if (polygon.length < 3) {
    return false;
  }

  let inside = false;
  let previous = polygon[polygon.length - 1]!;
  for (const current of polygon) {
    const crosses = (current.y > point.y) !== (previous.y > point.y)
      && point.x < ((previous.x - current.x) * (point.y - current.y)) / (previous.y - current.y) + current.x;
    if (crosses) {
      inside = !inside;
    }
    previous = current;
  }
  return inside;
}
